<template>
  <v-content class="client analytics explorer page">
    <v-progress-linear
      v-if="loading"
      :color="themes.client.light.input"
      indeterminate
      class="my-0"
    />

    <v-container>
      <v-layout column>
        <v-toolbar flat color="transparent">
          <v-toolbar-title>
            <div class="title">Analytics Explorer</div>
            <div class="caption grey--text text--darken-2" v-if="loading">Updating…</div>
            <div class="caption grey--text text--darken-2" v-else @click="refresh">
              <v-tooltip bottom>
                <span slot="activator">Last updated at {{ new Date(timestamp) | date("HH:mm 'on' d MMM yyyy") }}</span>
                <span>Click to refresh</span>
              </v-tooltip>
            </div>
          </v-toolbar-title>
          <v-spacer />
          <v-tooltip left>
            <span>Back to overview</span>
            <v-btn icon slot="activator" to="/client/analytics">
              <v-icon>mdi-arrow-left-bold</v-icon>
            </v-btn>
          </v-tooltip>
        </v-toolbar>
      </v-layout>
    </v-container>

    <v-container class="pt-0">
      <v-layout row wrap>
        <!-- Currently applied filters -->
        <v-flex xs6>
          <div class="title pt-2">Filters</div>
        </v-flex>
        <!-- Add filter dialog -->
        <v-flex xs6 class="text-xs-right">
          <v-dialog max-width="600" v-model="showfilters">
            <v-btn round outline small :color="themes.client.light.input" slot="activator" @click="filterSelection = null">
              <v-icon small left>mdi-plus</v-icon> Filter
            </v-btn>
            <v-card>
              <v-card-title>Select a filter to add</v-card-title>
              <v-card-text v-show="!filterSelection">
                <v-btn block @click="filterSelection = 'opportunity'"><v-icon left>mdi-clipboard-account</v-icon> Opportunity</v-btn>
                <v-btn block @click="filterSelection = 'set'"><v-icon left>mdi-counter</v-icon> Set</v-btn>
                <v-btn block @click="filterSelection = 'source'"><v-icon left>mdi-cursor-default-click</v-icon> Source</v-btn>
                <v-btn block @click="filterSelection = 'status'"><v-icon left>mdi-chart-gantt</v-icon> Status</v-btn>
                <v-btn block @click="filterSelection = 'completion'"><v-icon left>mdi-clipboard-check</v-icon> Completion</v-btn>
                <v-btn block @click="filterSelection = 'gender'"><v-icon left>mdi-gender-male-female</v-icon> Gender</v-btn>
                <v-btn block @click="filterSelection = 'ethnicity'"><v-icon left>mdi-account-multiple</v-icon> Ethnicity</v-btn>
                <v-btn block @click="filterSelection = 'institution'"><v-icon left>mdi-bank</v-icon> Institution</v-btn>
                <v-btn block @click="filterSelection = 'qualification'"><v-icon left>mdi-school</v-icon> Qualification</v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'opportunity'">
                <v-btn
                  v-for="opp in allOpportunities"
                  :key="`add-opportunity-${opp.id}`"
                  round outline small block
                  @click="toggleFilter('opportunity', opp.id)"
                  :color="filters.opportunity.indexOf(opp.id) > -1 ? themes.client.light.input : null"
                >
                  {{ opp.name }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'set'">
                <v-btn
                  v-for="set in sets"
                  :key="`add-set-${set}`"
                  round outline small block
                  @click="toggleFilter('set', set)"
                  :color="filters.set.indexOf(set) > -1 ? themes.client.light.input : null"
                >
                  {{ set }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'source'">
                <v-btn
                  v-for="source in allSources"
                  :key="`add-source-${source.value}`"
                  round outline small block
                  @click="toggleFilter('source', source.value)"
                  :color="filters.source.indexOf(source.value) > -1 ? themes.client.light.input : null"
                >
                  {{ source.text }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'status'">
                <v-btn
                  v-for="status in allStatuses"
                  :key="`add-status-${status}`"
                  round outline small block
                  @click="toggleFilter('status', status)"
                  :color="filters.status.indexOf(status) > -1 ? themes.client.light.input : null"
                >
                  {{ status }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'completion'">
                <v-radio-group>
                  <v-radio :value="null" label="All applications" @change="toggleFilter('completion', null)" />
                  <v-radio :value="true" label="Only complete" @change="toggleFilter('completion', true)" />
                  <v-radio :value="false" label="Only incomplete" @change="toggleFilter('completion', false)" />
                  </v-radio-group>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'gender'">
                <v-btn
                  v-for="gender in allGenders"
                  :key="`add-gender-${gender}`"
                  round outline small block
                  @click="toggleFilter('gender', gender)"
                  :color="filters.gender.indexOf(gender) > -1 ? themes.client.light.input : null"
                >
                  {{ gender }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'ethnicity'">
                <v-btn
                  v-for="ethnicity in allEthnicities"
                  :key="`add-ethnicity-${ethnicity}`"
                  round outline small block
                  @click="toggleFilter('ethnicity', ethnicity)"
                  :color="filters.ethnicity.indexOf(ethnicity) > -1 ? themes.client.light.input : null"
                >
                  {{ ethnicity }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'institution'">
                <v-btn
                  v-for="institution in allInstitutions"
                  :key="`add-institution-${institution}`"
                  round outline small block
                  @click="toggleFilter('institution', institution)"
                  :color="filters.institution.indexOf(institution) > -1 ? themes.client.light.input : null"
                >
                  {{ institution }}
                </v-btn>
              </v-card-text>
              <v-card-text v-show="filterSelection == 'qualification'">
                <v-btn
                  v-for="qualification in allQualifications"
                  :key="`add-qualification-${qualification}`"
                  round outline small block
                  @click="toggleFilter('qualification', qualification)"
                  :color="filters.qualification.indexOf(qualification) > -1 ? themes.client.light.input : null"
                >
                  {{ qualification }}
                </v-btn>
              </v-card-text>

              <v-card-actions v-show="filterSelection">
                <v-spacer />
                <v-btn small @click="showfilters = false" :class="themes.client.light.secondaryActionButton">
                  Done
                </v-btn>
                <v-spacer />
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
        <v-flex xs12>
          <div v-if="filters.opportunity">
            <v-chip v-for="id in filters.opportunity" :key="`opportunity-${id}`"
              small close @input="toggleFilter('opportunity', id)">
              <v-avatar><v-icon small>mdi-clipboard-account</v-icon></v-avatar>
              {{ opportunities.find((o) => o.id == id) ? opportunities.find((o) => o.id == id).name : id }}
            </v-chip>
          </div>
          <div v-if="filters.set">
            <v-chip v-for="set in filters.set" :key="`set-${set}`"
              small close @input="toggleFilter('set', set)">
              <v-avatar><v-icon small>mdi-counter</v-icon></v-avatar>
              {{ set }}
            </v-chip>
          </div>
          <div v-if="filters.source">
            <v-chip v-for="source in filters.source" :key="`source-${source}`"
              small close @input="toggleFilter('source', source)">
              <v-avatar><v-icon small>mdi-cursor-default-click</v-icon></v-avatar>
              {{ source }}
            </v-chip>
          </div>
          <div v-if="filters.status">
            <v-chip v-for="status in filters.status" :key="`status-${status}`"
              small close @input="toggleFilter('status', status)">
              <v-avatar><v-icon small>mdi-chart-gantt</v-icon></v-avatar>
              {{ status }}
            </v-chip>
          </div>
          <div v-if="filters.completion == true">
            <v-chip
              small close @input="toggleFilter('completion', null)">
              <v-avatar><v-icon small>mdi-clipboard-check</v-icon></v-avatar>
              Complete
            </v-chip>
          </div>
          <div v-if="filters.completion == false">
            <v-chip
              small close @input="toggleFilter('completion', null)">
              <v-avatar><v-icon small>mdi-clipboard-remove</v-icon></v-avatar>
              Incomplete
            </v-chip>
          </div>
          <div v-if="filters.gender">
            <v-chip v-for="gender in filters.gender" :key="`gender-${gender}`"
              small close @input="toggleFilter('gender', gender)">
              <v-avatar><v-icon small>mdi-gender-male-female</v-icon></v-avatar>
              {{ gender }}
            </v-chip>
          </div>
          <div v-if="filters.ethnicity">
            <v-chip v-for="ethnicity in filters.ethnicity" :key="`ethnicity-${ethnicity}`"
              small close @input="toggleFilter('ethnicity', ethnicity)">
              <v-avatar><v-icon small>mdi-account-multiple</v-icon></v-avatar>
              {{ ethnicity }}
            </v-chip>
          </div>
          <div v-if="filters.institution">
            <v-chip v-for="institution in filters.institution" :key="`institution-${institution}`"
              small close @input="toggleFilter('institution', institution)">
              <v-avatar><v-icon small>mdi-bank</v-icon></v-avatar>
              {{ institution }}
            </v-chip>
          </div>
          <div v-if="filters.qualification">
            <v-chip v-for="qualification in filters.qualification" :key="`qualification-${qualification}`"
              small close @input="toggleFilter('qualification', qualification)">
              <v-avatar><v-icon small>mdi-school</v-icon></v-avatar>
              {{ qualification }}
            </v-chip>
          </div>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- All data filtered out -->
    <v-container v-if="analytics.length > 0 && filteredAnalytics.length == 0">
      <v-alert color="warning" :value="true" icon="mdi-tray-alert">
        Your filter criteria has eliminated all applications.<br>
        Please relax the conditions until some applications match to see the
        relevant analytics.
      </v-alert>
    </v-container>

    <v-container grid-list-xl v-if="filteredAnalytics.length > 0">
      <v-layout row wrap>
        <v-flex xs12 v-show="filters.opportunity.length != 1">
          <v-card><v-card-text>
            <apex
              type="pie"
              width="100%"
              :height="Object.keys(opportunityCounts).length < 20 ? 400 : Object.keys(opportunityCounts).length * 18"
              :options="{
                title: { text: 'Opportunities', style: { fontSize: '24px' } },
                labels: Object.keys(opportunityCounts),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                tooltip: { enabled: true },
              }"
              :series="Object.values(opportunityCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 lg6 v-show="filters.gender.length != 1">
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Genders', style: { fontSize: '24px' } },
                labels: Object.keys(genderCounts),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(genderCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 lg6 v-show="filters.ethnicity.length != 1">
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Ethnicities', style: { fontSize: '24px' } },
                labels: Object.keys(ethnicityCounts),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(ethnicityCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 lg6 v-show="filters.status.length != 1">
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Application Status', style: { fontSize: '24px' } },
                labels: Object.keys(validityCounts),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(validityCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 lg6 v-show="filters.set.length != 1">
          <v-card><v-card-text>
            <apex
              type="donut"
              width="100%"
              height="400px"
              :options="{
                title: { text: 'Application Sets', style: { fontSize: '24px' } },
                labels: Object.keys(setCounts),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: { pie: { donut: { labels: { show: true, total: { show: true } } } } },
                tooltip: { enabled: false },
              }"
              :series="Object.values(setCounts)"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 v-show="filters.source.length != 1">
          <v-card><v-card-text>
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(sourcesCounts.length)"
              :options="{
                title: { text: 'Applicant Sources', style: { fontSize: '24px' } },
                labels: sourcesCounts.map(o => o[0]),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: sourcesCounts.map(o => o[1]) }]"
            />
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 v-show="filters.institution.length != 1">
          <v-card><v-card-text :class="{ 'pb-0' : hiddenInstitutionCount > 0 }">
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(topInstitutionCounts.length)"
              :options="{
                title: { text: 'Top Institutions', style: { fontSize: '24px' } },
                labels: topInstitutionCounts.map(o => o[0]),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: topInstitutionCounts.map(o => o[1]) }]"
            />
          </v-card-text><v-card-text v-if="hiddenInstitutionCount" class="text-xs-center caption pt-0 pb-2">
            <v-icon small>mdi-information-variant</v-icon>
            {{ hiddenInstitutionCount }} items have been omitted
          </v-card-text></v-card>
        </v-flex>

        <v-flex xs12 v-show="filters.qualification.length != 1">
          <v-card><v-card-text :class="{ 'pb-0' : hiddenQualificationCount > 0 }">
            <apex
              type="bar"
              width="100%"
              :height="linearChartSize(topQualificationCounts.length)"
              :options="{
                title: { text: 'Top Qualifications', style: { fontSize: '24px' } },
                labels: topQualificationCounts.map(o => o[0]),
                colors,
                chart: { fontFamily: 'Roboto, sans-serif' },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    dataLabels: { position: 'top' }
                  },
                },
                dataLabels: {
                  enabled: true,
                  style: { colors: ['#666'] },
                  offsetX: 24,
                },
                tooltip: { enabled: false },
                xaxis: {
                  labels: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: { minWidth: 200, maxWidth: 300 },
                }
              }"
              :series="[{ data: topQualificationCounts.map(o => o[1]) }]"
            />
          </v-card-text><v-card-text v-if="hiddenQualificationCount" class="text-xs-center caption pt-0 pb-2">
            <v-icon small>mdi-information-variant</v-icon>
            {{ hiddenQualificationCount }} items have been omitted
          </v-card-text></v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-content>
</template>

<script>
// Mixins
import { managedDocumentAttributes } from '@/mixins/managedDocumentAttributes.js'
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

// Data
import colors from '@/data/colors'

// Components
import { mapGetters } from 'vuex'

// Helpers
import _ from 'lodash'

/* Application Structure */
// [
//   id,                       // 0
//   opportunity_id,           // 1
//   complete,                 // 2
//   eligible,                 // 3
//   gender,                   // 4
//   ethnicity,                // 5
//   institution_name,         // 6
//   qualification_type_name,  // 7
//   status,                   // 8
//   campaign_slug,            // 9
//   opportunity_set,          // 10
// ]

// Vue Component
export default {
  mixins: [
    managedDocumentAttributes,
    managedQueryParameters
  ],

  created() {
    this.$store.dispatch('client/organization/fetch', this.$route.params.id).then(() => {
      this.documentAttributes.title = 'Analytics Explorer - Leaply'
    })
    this.$store.dispatch('client/opportunities/fetchAll')
    this.$store.dispatch('client/campaigns/fetchAll')
    if (!this.timestamp) {
      this.$store.dispatch('client/analytics/fetchAll')
    }
    this.initializeFilters()
  },
  data() {
    return {
      colors: colors.analytics,
      // Filter interface
      showfilters: false,
      filterSelection: null,
      filters: {
        opportunity: [],
        set: [],
        status: [],
        completion: null,
        gender: [],
        ethnicity: [],
        source: [],
        institution: [],
        qualification: [],
      }
    }
  },
  computed: {
    ...mapGetters({
      timestamp: 'client/analytics/timestamp',
      loading: 'client/analytics/loading',
      analytics: 'client/analytics/all',
      opportunities: 'client/opportunities/all',
      organization: 'client/organization/model',
      campaigns: 'client/campaigns/all',
    }),
    sets() {
      return [...new Set(this.opportunities.map(o => o.set).filter(s => s))].sort()
    },
    filteredAnalytics() {
      return this.analytics.filter(app => {
        return (!this.filters.set.length || this.filters.set.includes(app[10])) &&
               (!this.filters.gender.length || this.filters.gender.includes(app[4])) &&
               (!this.filters.ethnicity.length || this.filters.ethnicity.includes(app[5])) &&
               (!this.filters.source.length || this.filters.source.includes(app[9])) &&
               (!this.filters.status.length || this.filters.status.includes(app[8])) &&
               (this.filters.completion == null || this.filters.completion == app[2]) &&
               (!this.filters.institution.length || this.filters.institution.includes(app[6])) &&
               (!this.filters.qualification.length || this.filters.qualification.includes(app[7])) &&
               (!this.filters.opportunity.length || this.filters.opportunity.includes(app[1]))
      })
    },
    // Filter options
    allOpportunities() { return _.sortBy(this.opportunities, ['name']) },
    allGenders() { return [...new Set(this.analytics.map(a => a[4]))].sort() },
    allEthnicities() { return [...new Set(this.analytics.map(a => a[5]))].sort() },
    allSources() { return [...new Set(this.analytics.map(a => a[9]))].sort().map(s => ({ text: s || 'Leaply', value: s })) },
    allStatuses() { return [...new Set(this.analytics.map(a => a[8]))].sort() },
    allInstitutions() { return [...new Set(this.analytics.map(a => a[6]))].sort() },
    allQualifications() { return [...new Set(this.analytics.map(a => a[7]))].sort() },
    // Counts
    opportunityCounts() {
      let keyMap = {}
      this.opportunities.forEach(opportunity => keyMap[opportunity.id] = opportunity.name)

      let counts = {}
      this.filteredAnalytics.forEach(application => {
        const name = keyMap[application[1]]
        counts[name] = (counts[name] || 0) + 1
      })

      return counts
    },
    genderCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        let gender = application[4]
        if (!gender) { gender = 'Unspecified' }
        counts[gender] = (counts[gender] || 0) + 1
      })

      return counts
    },
    ethnicityCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        let ethnicity = application[5]
        if (!ethnicity) { ethnicity = 'Unspecified' }
        counts[ethnicity] = (counts[ethnicity] || 0) + 1
      })

      return counts
    },
    institutionCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        let institution = application[6]
        if (!institution) { institution = 'Unspecified' }
        counts[institution] = (counts[institution] || 0) + 1
      })

      let sorted = []
      for (let institution in counts) { sorted.push([institution, counts[institution]]) }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
    topInstitutionCounts() {
      const desired = 20
      if (this.institutionCounts.length < desired) {
        return this.institutionCounts
      } else {
        let top = []
        for (var i = 0; i < this.institutionCounts.length; i++) {
          if (top.length < desired) {
            top.push(this.institutionCounts[i])
          } else {
            break // for
          }
        }
        return top
      }
    },
    hiddenInstitutionCount() {
      if (this.institutionCounts.length === this.topInstitutionCounts.length) {
        return 0
      } else {
        const tops = _.sum(this.topInstitutionCounts.map(([n, c]) => c))
        return this.filteredAnalytics.length - tops
      }
    },
    qualificationCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        let qualification = application[7]
        if (!qualification) { qualification = 'Unspecified' }
        counts[qualification] = (counts[qualification] || 0) + 1
      })

      let sorted = []
      for (let qualification in counts) { sorted.push([qualification, counts[qualification]]) }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
    topQualificationCounts() {
      const desired = 20
      if (this.qualificationCounts.length < desired) {
        return this.qualificationCounts
      } else {
        let top = []
        for (var i = 0; i < this.qualificationCounts.length; i++) {
          if (top.length < desired) {
            top.push(this.qualificationCounts[i])
          } else {
            break // for
          }
        }
        return top
      }
    },
    hiddenQualificationCount() {
      if (this.qualificationCounts.length === this.topQualificationCounts.count) {
        return 0
      } else {
        const tops = _.sum(this.topQualificationCounts.map(([n, c]) => c))
        return this.filteredAnalytics.length - tops
      }
    },
    validityCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        const complete = application[2]
        const eligible = application[3]
        const status = application[8]
        if (status === 'Withdrawn') {
          counts['Withdrawn'] = (counts['Withdrawn'] || 0) + 1
        } else if (complete && eligible) {
          counts['Completed'] = (counts['Completed'] || 0) + 1
        } else if (eligible) {
          counts['Incomplete'] = (counts['Incomplete'] || 0) + 1
        } else {
          counts['Ineligible'] = (counts['Ineligible'] || 0) + 1
        }
      })
      return counts
    },
    setCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        const setName = application[10]

        counts[setName] = (counts[setName] || 0) + 1
      })
      return counts
    },
    sourcesCounts() {
      let counts = {}
      this.filteredAnalytics.forEach(application => {
        let source = application[9]
        if (!source) { source = 'leaply' }
        counts[source] = (counts[source] || 0) + 1
      })
      let sorted = []
      for (let source in counts) { sorted.push([source, counts[source]]) }
      sorted.sort((a, b) => b[1] - a[1])
      return sorted
    },
  },
  methods: {
    linearChartSize(count) {
      return 100 + count * 20
    },
    refresh() {
      this.$store.dispatch('client/analytics/fetchAll')
    },
    initializeFilters() {
      Object.keys(this.filters).forEach(f => {
        // No value
        if (!this.initialQueryParameters[f]) {
          return
        }

        // Filter type is array and query param is array
        if (typeof this.initialQueryParameters[f] === 'object') {
          this.filters[f] = this.initialQueryParameters[f].map(param =>
            f == 'opportunity' ? Number(param) : param
          )
          return
        }

        // Other
        this.filters[f] = this.initialQueryParameters[f]
      })
    },
    toggleFilter(filter, value) {
      // Boolean filter
      if ([null, true, false].indexOf(value) > -1) {
        this.filters[filter] = value
      } else if (this.filters[filter].indexOf(value) > -1) {
        // Exists, toggle off
        this.filters[filter].splice(this.filters[filter].indexOf(value), 1)
      } else {
        // Doesn't exist, toggle on
        this.filters[filter].push(value)
      }
      this.updateQueryParameters(this.filters)
    },
  },
}
</script>

<style lang="scss">
.client.analytics.page {
  .v-toolbar__content {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
